import React from 'react';
import Spinner from '@omilia/spinner';
import {
  SCloseIcon,
  SFilePreview,
  SBackground,
  SPreviewContainer,
  SFileStatus,
  SFileUtterance,
  SStatusMessage,
  SErrorMessage,
} from './FilePreview.styled';
import { FilePreviewData, MessageStatus } from 'types';
import { FileContent } from './components/FileContent';
import { useFilePreview } from './useFilePreview';
import { useTranslation } from '@omilia/providers';
import useKeyboard from '@omilia/use-keyboard';

interface FilePreviewProps {
  asMessage?: boolean;
  data: FilePreviewData;
  status?: MessageStatus;
  resetFile?: () => void;
  errorMessage?: string;
  fileWidth?: number;
  isLast?: boolean;
  isMultipleFiles?: boolean;
}

export default function FilePreview({
  asMessage = false,
  data,
  status = MessageStatus.PENDING,
  resetFile,
  errorMessage,
  fileWidth = 170,
  isLast = false,
  isMultipleFiles = false,
}: FilePreviewProps) {
  const { isError, closeIconColor } = useFilePreview(errorMessage, status);
  const { t } = useTranslation();

  const keyboardProps = useKeyboard({
    handlers: {
      Enter: (e: KeyboardEvent) => {
        if (resetFile) {
          e.stopPropagation();
          resetFile();
        }
      },
    },
  });

  const getFileStatusMessage = () => {
    if (asMessage && status === MessageStatus.PENDING)
      return (
        <SStatusMessage>
          {t('files.uploading')} <Spinner thin color="secondary" />
        </SStatusMessage>
      );
    if (status === MessageStatus.ERROR || errorMessage)
      return (
        <SErrorMessage asMessage={asMessage}>{errorMessage}</SErrorMessage>
      );
    if (asMessage && status === MessageStatus.SENT)
      return (
        <SStatusMessage>
          {isMultipleFiles ? t('files.filesUploaded') : t('files.fileUploaded')}
        </SStatusMessage>
      );

    return null;
  };

  const statusMessage = getFileStatusMessage();

  return (
    <SPreviewContainer asMessage={asMessage}>
      <SBackground isError={isError} asMessage={asMessage} status={status}>
        <SFilePreview isError={isError} asMessage={asMessage}>
          <FileContent
            data={data}
            fileWidth={fileWidth}
            status={status}
            isError={isError}
            asMessage={asMessage}
          />

          {resetFile && (
            <SCloseIcon
              {...keyboardProps}
              primaryColor={closeIconColor}
              size="16"
              onClick={resetFile}
            />
          )}
        </SFilePreview>

        {isLast && data?.utterance && (
          <SFileUtterance status={status}>{data.utterance}</SFileUtterance>
        )}
      </SBackground>

      {(!asMessage || isLast) && (
        <SFileStatus withStatus={!!statusMessage}>{statusMessage}</SFileStatus>
      )}
    </SPreviewContainer>
  );
}
